import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Building2, Users, FileText, Upload, Map, Shield,
  Check, X, Edit, Trash2, Plus, ChevronDown, AlertTriangle, ChevronLeft, ChevronRight,
  FolderOpen, Download, Filter,
} from "lucide-react";
import { toast } from "sonner";
import HotspotEditor from "@/components/HotspotEditor";

/* ─── Admin Login ──────────────────────────────────────────────────────────── */
function AdminLoginForm({ onLogin }) {
  const { adminLogin } = useAuth();
  const [email, setEmail]       = useState("bilalzaidi84@gmail.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await adminLogin(email, password);
      onLogin();
      toast.success("Signed in as admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F9F8F6] dark:bg-[#0F1115] p-6">
      <div className="w-full max-w-sm bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-8 shadow-lg">
        <div className="h-1 w-full -mx-8 -mt-8 mb-8 bg-gradient-to-r from-[#9C7C54] via-[#C0A062] to-[#9C7C54]" />
        <div className="flex items-center gap-2 mb-6">
          <Shield size={18} className="text-[#9C7C54]" />
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#9C7C54]">Admin Access</span>
        </div>
        <h2 className="font-heading text-2xl font-light text-[#1A1A1A] dark:text-[#EAEAEA] mb-6">Sign In</h2>
        <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-4">
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">Email</label>
            <input
              data-testid="admin-email-input"
              type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] bg-[#F9F8F6] dark:bg-[#0F1115] text-sm text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] rounded-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-1.5">Password</label>
            <input
              data-testid="admin-password-input"
              type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="admin123"
              className="w-full px-3 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] bg-[#F9F8F6] dark:bg-[#0F1115] text-sm text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] rounded-sm"
              required
            />
          </div>
          {error && <p className="text-xs text-red-500" data-testid="admin-login-error">{error}</p>}
          <button
            data-testid="admin-login-submit-btn"
            type="submit" disabled={loading}
            className="w-full py-2.5 bg-[#9C7C54] text-white text-sm tracking-wide hover:bg-[#7a5f3e] disabled:opacity-50 transition-colors rounded-sm"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Stats Card ───────────────────────────────────────────────────────────── */
function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-5 animate-slide-up">
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#737373] mb-2">{label}</div>
      <div className="font-heading text-3xl font-light text-[#1A1A1A] dark:text-[#EAEAEA]">{value}</div>
      {sub && <div className="text-xs text-[#9C7C54] mt-1">{sub}</div>}
    </div>
  );
}

/* ─── Main Admin Panel ─────────────────────────────────────────────────────── */
export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats]         = useState(null);
  const [units, setUnits]         = useState([]);
  const [owners, setOwners]       = useState([]);
  const [requests, setRequests]   = useState([]);
  const [editUnit, setEditUnit]   = useState(null);
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [importData, setImportData]  = useState(null);
  const [confirming, setConfirming]  = useState(false);
  const [floorPlans, setFloorPlans]  = useState([]);
  const [hotspotEditor, setHotspotEditor] = useState(null); // {floor, wing}
  const [documents, setDocuments]  = useState([]);
  const [docFilterUnit, setDocFilterUnit] = useState("");
  const [docFilterType, setDocFilterType] = useState("");
  const [unitsPage, setUnitsPage]  = useState(1);
  const UNITS_PAGE_SIZE = 25;
  const fileInputRef = useRef(null);
  const fpRefs = { GF: useRef(null), FFR: useRef(null), FFL: useRef(null) };

  const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};

  const fetchAll = () => {
    axios.get(`${API}/admin/stats`, { headers }).then((r) => setStats(r.data)).catch(console.error);
    axios.get(`${API}/units`, { headers }).then((r) => setUnits(r.data)).catch(console.error);
    axios.get(`${API}/owners`, { headers }).then((r) => setOwners(r.data)).catch(console.error);
    axios.get(`${API}/change-requests`, { headers }).then((r) => setRequests(r.data)).catch(console.error);
    axios.get(`${API}/floor-plans`, { headers }).then((r) => setFloorPlans(r.data)).catch(console.error);
    axios.get(`${API}/admin/documents`, { headers }).then((r) => setDocuments(r.data)).catch(console.error);
  };

  useEffect(() => {
    if (user?.role === "admin") fetchAll();
  }, [user]);

  if (!user) return <AdminLoginForm onLogin={() => navigate("/admin")} />;
  if (user.role !== "admin") return <AdminLoginForm onLogin={() => navigate("/admin")} />;

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/change-requests/${id}/approve`, { notes: "Approved by admin" }, { headers });
      toast.success("Request approved");
      fetchAll();
    } catch { toast.error("Failed to approve"); }
  };

  const handleReject = async (id) => {
    const notes = prompt("Rejection reason (optional):");
    try {
      await axios.put(`${API}/change-requests/${id}/reject`, { notes: notes || "" }, { headers });
      toast.success("Request rejected");
      fetchAll();
    } catch { toast.error("Failed to reject"); }
  };

  const handleDeleteUnit = async (unitId) => {
    if (!window.confirm(`Delete unit ${unitId}?`)) return;
    try {
      await axios.delete(`${API}/units/${unitId}`, { headers });
      toast.success(`Unit ${unitId} deleted`);
      fetchAll();
    } catch { toast.error("Delete failed"); }
  };

  const handleUpdateUnit = async (unitId, data) => {
    try {
      await axios.put(`${API}/units/${unitId}`, data, { headers });
      toast.success("Unit updated");
      setEditUnit(null);
      fetchAll();
    } catch { toast.error("Update failed"); }
  };

  const handleImport = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/import/excel`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setImportData(data);
      toast.success(`Parsed ${data.parsed?.length || 0} rows`);
    } catch (err) { toast.error("Import failed: " + (err.response?.data?.detail || "")); }
  };

  const handleFloorPlanUpload = async (floor, wing, file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      await axios.post(`${API}/floor-plans?floor=${floor}&wing=${wing}`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Floor plan uploaded for ${floor}${wing ? " · " + wing : ""}`);
      fetchAll();
    } catch (err) { toast.error("Upload failed: " + (err.response?.data?.detail || "")); }
  };

  const handleConfirmImport = async () => {
    if (!importData?.import_id) return;
    setConfirming(true);
    try {
      const { data } = await axios.post(`${API}/import/confirm/${importData.import_id}`, {}, { headers });
      toast.success(data.message);
      setImportData(null);
      fetchAll();
    } catch (err) {
      toast.error("Confirm failed: " + (err.response?.data?.detail || ""));
    } finally {
      setConfirming(false);
    }
  };

  const handleDocDownload = async (d) => {
    try {
      const resp = await axios.get(`${API}/files/${d.storage_path}`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], {
        type: d.content_type || resp.headers["content-type"] || "application/octet-stream",
      }));
      const a  = document.createElement("a");
      a.href     = url;
      a.download = d.original_filename || `${d.document_type}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const TABS = [
    { id: "dashboard", label: "Dashboard",    icon: <LayoutDashboard size={15} /> },
    { id: "units",     label: "Units",         icon: <Building2 size={15} /> },
    { id: "owners",    label: "Owners",         icon: <Users size={15} /> },
    { id: "requests",  label: "Requests",       icon: <FileText size={15} />, badge: requests.filter(r => r.status === "pending").length },
    { id: "documents", label: "Documents",      icon: <FolderOpen size={15} />, badge: documents.length },
    { id: "import",    label: "Import",         icon: <Upload size={15} /> },
    { id: "floorplans", label: "Floor Plans",   icon: <Map size={15} /> },
  ];

  const STATUS_CHIP = {
    pending:  "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400",
    approved: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700",
    rejected: "bg-red-100 dark:bg-red-900/40 text-red-600",
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F8F6] dark:bg-[#0F1115]" data-testid="admin-panel">
      <div className="max-w-screen-xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-[#9C7C54] font-semibold mb-1">Admin Panel</div>
            <h1 className="font-heading text-2xl font-light text-[#1A1A1A] dark:text-[#EAEAEA]">
              Highline Directory Control
            </h1>
          </div>
          <div className="flex items-center gap-2 text-xs text-[#737373]">
            <Shield size={13} className="text-[#9C7C54]" />
            {user.email}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 border-b border-[#E5E5E5] dark:border-[#272A30]">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 transition-all whitespace-nowrap ${
                activeTab === t.id
                  ? "border-[#9C7C54] text-[#9C7C54] font-semibold"
                  : "border-transparent text-[#737373] hover:text-[#1A1A1A] dark:hover:text-[#EAEAEA]"
              }`}
            >
              {t.icon}
              {t.label}
              {t.badge > 0 && (
                <span className="bg-[#9C7C54] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── DASHBOARD ─────────────────────────────────────────────────────── */}
        {activeTab === "dashboard" && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <StatCard label="Total Units"    value={stats?.total_units ?? "—"} />
              <StatCard label="Occupied"       value={stats?.occupied_units ?? "—"} sub="units" />
              <StatCard label="Vacant"         value={stats?.vacant_units ?? "—"} sub="units" />
              <StatCard label="Total Owners"   value={stats?.total_owners ?? "—"} />
              <StatCard label="Pending Reqs"   value={stats?.pending_requests ?? "—"} sub="awaiting review" />
              <StatCard label="GF / FF"        value={`${stats?.gf_units ?? "—"} / ${stats?.ff_units ?? "—"}`} sub="units by floor" />
            </div>
            <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-6">
              <h3 className="font-heading text-lg font-light text-[#1A1A1A] dark:text-[#EAEAEA] mb-3">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => setActiveTab("units")} className="px-4 py-2 bg-[#9C7C54] text-white text-xs tracking-wide hover:bg-[#7a5f3e] transition-colors rounded-sm">Manage Units</button>
                <button onClick={() => setActiveTab("requests")} className="px-4 py-2 border border-[#9C7C54] text-[#9C7C54] text-xs tracking-wide hover:bg-[#9C7C54] hover:text-white transition-colors rounded-sm">Review Requests ({stats?.pending_requests || 0})</button>
                <button onClick={() => setActiveTab("import")} className="px-4 py-2 border border-[#E5E5E5] dark:border-[#272A30] text-[#737373] text-xs tracking-wide hover:border-[#9C7C54] hover:text-[#9C7C54] transition-colors rounded-sm">Import Excel</button>
                <button onClick={() => setActiveTab("floorplans")} className="px-4 py-2 border border-[#E5E5E5] dark:border-[#272A30] text-[#737373] text-xs tracking-wide hover:border-[#9C7C54] hover:text-[#9C7C54] transition-colors rounded-sm">Upload Floor Plans</button>
              </div>
            </div>
          </div>
        )}

        {/* ── UNITS ────────────────────────────────────────────────────────── */}
        {activeTab === "units" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-light text-[#1A1A1A] dark:text-[#EAEAEA]">
                Units ({units.length})
              </h2>
              <button
                data-testid="add-unit-btn"
                onClick={() => setShowAddUnit(!showAddUnit)}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#9C7C54] text-white text-xs tracking-wide hover:bg-[#7a5f3e] transition-colors rounded-sm"
              >
                <Plus size={13} /> Add Unit
              </button>
            </div>

            {showAddUnit && (
              <AddUnitForm
                onAdd={async (data) => {
                  try {
                    await axios.post(`${API}/units`, data, { headers });
                    toast.success("Unit added");
                    setShowAddUnit(false);
                    fetchAll();
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                }}
                onCancel={() => setShowAddUnit(false)}
              />
            )}

            <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] overflow-x-auto">
              <table className="w-full text-sm" data-testid="units-table">
                <thead>
                  <tr className="bg-[#F5F3F0] dark:bg-[#0F1115] border-b border-[#E5E5E5] dark:border-[#272A30]">
                    {["Unit ID","Floor","Wing","Size (sqft)","Status","Owners","Actions"].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-left text-[10px] tracking-[0.15em] uppercase text-[#737373] font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {units.slice((unitsPage - 1) * UNITS_PAGE_SIZE, unitsPage * UNITS_PAGE_SIZE).map((u) => (
                    <tr key={u.id} data-testid={`unit-row-${u.unit_id}`}
                      className="border-b border-[#E5E5E5] dark:border-[#272A30] hover:bg-[#F9F8F6] dark:hover:bg-[#1A1D24] transition-colors">
                      <td className="px-4 py-2.5 font-semibold text-[#1A1A1A] dark:text-[#EAEAEA]">{u.unit_id}</td>
                      <td className="px-4 py-2.5 text-[#737373]">{u.floor}</td>
                      <td className="px-4 py-2.5 text-[#737373]">{u.wing || "—"}</td>
                      <td className="px-4 py-2.5 text-[#737373]">{u.size_sqft > 0 ? u.size_sqft.toFixed(2) : "—"}</td>
                      <td className="px-4 py-2.5">
                        {editUnit?.unit_id === u.unit_id ? (
                          <select
                            value={editUnit.occupancy_status}
                            onChange={(e) => setEditUnit({ ...editUnit, occupancy_status: e.target.value })}
                            className="px-2 py-1 border border-[#9C7C54] bg-white dark:bg-[#1A1D24] text-xs rounded-sm focus:outline-none"
                          >
                            <option value="occupied">occupied</option>
                            <option value="vacant">vacant</option>
                          </select>
                        ) : (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${u.occupancy_status === "occupied" ? "badge-occupied" : "badge-vacant"}`}>
                            {u.occupancy_status}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-[#737373] max-w-[160px] truncate">
                        {u.owners.map((o) => o.name).join(", ") || "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        {editUnit?.unit_id === u.unit_id ? (
                          <div className="flex gap-1.5">
                            <button onClick={() => handleUpdateUnit(u.unit_id, { occupancy_status: editUnit.occupancy_status })}
                              className="p-1.5 bg-emerald-500 text-white rounded-sm hover:bg-emerald-600 transition-colors" title="Save">
                              <Check size={12} />
                            </button>
                            <button onClick={() => setEditUnit(null)}
                              className="p-1.5 bg-[#E5E5E5] dark:bg-[#272A30] text-[#737373] rounded-sm hover:bg-red-100 transition-colors" title="Cancel">
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-1.5">
                            <button
                              data-testid={`edit-unit-${u.unit_id}`}
                              onClick={() => setEditUnit({ unit_id: u.unit_id, occupancy_status: u.occupancy_status })}
                              className="p-1.5 text-[#737373] hover:text-[#9C7C54] hover:bg-[#F5EDE0] dark:hover:bg-[#2A2318] rounded-sm transition-colors"
                              title="Edit"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              data-testid={`delete-unit-${u.unit_id}`}
                              onClick={() => handleDeleteUnit(u.unit_id)}
                              className="p-1.5 text-[#737373] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-sm transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Units pagination */}
            {units.length > UNITS_PAGE_SIZE && (
              <div className="flex items-center justify-between mt-3 px-1">
                <span className="text-[10px] text-[#737373]">
                  Showing {(unitsPage - 1) * UNITS_PAGE_SIZE + 1}–{Math.min(unitsPage * UNITS_PAGE_SIZE, units.length)} of {units.length}
                </span>
                <div className="flex items-center gap-1">
                  <button data-testid="units-page-prev" onClick={() => setUnitsPage(p => Math.max(1, p - 1))}
                    disabled={unitsPage === 1}
                    className="w-7 h-7 flex items-center justify-center border border-[#E5E5E5] dark:border-[#272A30] text-[#737373] hover:border-[#9C7C54] hover:text-[#9C7C54] disabled:opacity-40 rounded-sm">
                    <ChevronLeft size={13} />
                  </button>
                  <span className="text-[10px] px-2 text-[#1A1A1A] dark:text-[#EAEAEA]">
                    {unitsPage}/{Math.ceil(units.length / UNITS_PAGE_SIZE)}
                  </span>
                  <button data-testid="units-page-next" onClick={() => setUnitsPage(p => Math.min(Math.ceil(units.length / UNITS_PAGE_SIZE), p + 1))}
                    disabled={unitsPage === Math.ceil(units.length / UNITS_PAGE_SIZE)}
                    className="w-7 h-7 flex items-center justify-center border border-[#E5E5E5] dark:border-[#272A30] text-[#737373] hover:border-[#9C7C54] hover:text-[#9C7C54] disabled:opacity-40 rounded-sm">
                    <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── OWNERS ───────────────────────────────────────────────────────── */}
        {activeTab === "owners" && (
          <div>
            <h2 className="font-heading text-xl font-light text-[#1A1A1A] dark:text-[#EAEAEA] mb-4">Owners ({owners.length})</h2>
            <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] overflow-x-auto">
              <table className="w-full text-sm" data-testid="owners-table">
                <thead>
                  <tr className="bg-[#F5F3F0] dark:bg-[#0F1115] border-b border-[#E5E5E5] dark:border-[#272A30]">
                    {["Name","Phone","Linked Units","Registered"].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-left text-[10px] tracking-[0.15em] uppercase text-[#737373] font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {owners.map((o) => (
                    <tr key={o.id} data-testid={`owner-row-${o.id}`}
                      className="border-b border-[#E5E5E5] dark:border-[#272A30] hover:bg-[#F9F8F6] dark:hover:bg-[#1A1D24] transition-colors">
                      <td className="px-4 py-2.5 font-medium text-[#1A1A1A] dark:text-[#EAEAEA]">{o.full_name}</td>
                      <td className="px-4 py-2.5 font-mono text-[#737373] text-xs">{o.phone}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {o.unit_ids.map((uid) => (
                            <span key={uid} className="text-[10px] bg-[#F5EDE0] dark:bg-[#2A2318] text-[#9C7C54] px-1.5 py-0.5 rounded-sm">{uid}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-[#737373]">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── REQUESTS ─────────────────────────────────────────────────────── */}
        {activeTab === "requests" && (
          <div>
            <h2 className="font-heading text-xl font-light text-[#1A1A1A] dark:text-[#EAEAEA] mb-4">
              Change Requests ({requests.length})
            </h2>
            {requests.length === 0 ? (
              <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-12 text-center text-[#737373]">
                No change requests
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map((req) => (
                  <div key={req.id} data-testid={`admin-req-${req.id}`}
                    className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-semibold text-[#1A1A1A] dark:text-[#EAEAEA]">{req.unit_id}</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${STATUS_CHIP[req.status] || ""}`}>
                            {req.status}
                          </span>
                          <span className="text-[10px] text-[#737373]">from {req.requester_name} ({req.requester_phone})</span>
                        </div>
                        <div className="text-xs text-[#737373] mb-1">
                          <strong>Requested:</strong>{" "}
                          {Object.entries(req.requested_fields || {}).map(([k, v]) => `${k} → "${v}"`).join(", ")}
                        </div>
                        {req.notes && <div className="text-xs text-[#737373]"><strong>Notes:</strong> {req.notes}</div>}
                        {req.admin_notes && <div className="text-xs text-[#9C7C54] mt-1"><strong>Admin:</strong> {req.admin_notes}</div>}
                        <div className="text-[10px] text-[#737373] mt-1">
                          {req.created_at ? new Date(req.created_at).toLocaleString() : ""}
                        </div>
                      </div>
                      {req.status === "pending" && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            data-testid={`approve-req-${req.id}`}
                            onClick={() => handleApprove(req.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-xs hover:bg-emerald-600 transition-colors rounded-sm"
                          >
                            <Check size={12} /> Approve
                          </button>
                          <button
                            data-testid={`reject-req-${req.id}`}
                            onClick={() => handleReject(req.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-xs hover:bg-red-600 transition-colors rounded-sm"
                          >
                            <X size={12} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── DOCUMENTS ────────────────────────────────────────────────────── */}
        {activeTab === "documents" && (
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-heading text-xl font-light text-[#1A1A1A] dark:text-[#EAEAEA]">
                Owner Documents
                <span className="text-sm text-[#737373] ml-2">({documents.length})</span>
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <Filter size={13} className="text-[#737373]" />
                <div className="relative">
                  <select
                    data-testid="doc-filter-unit"
                    value={docFilterUnit}
                    onChange={(e) => setDocFilterUnit(e.target.value)}
                    className="pl-3 pr-8 py-1.5 text-xs border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] appearance-none rounded-sm"
                  >
                    <option value="">All units</option>
                    {[...new Set(documents.map(d => d.unit_id))].sort().map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                  <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
                </div>
                <div className="relative">
                  <select
                    data-testid="doc-filter-type"
                    value={docFilterType}
                    onChange={(e) => setDocFilterType(e.target.value)}
                    className="pl-3 pr-8 py-1.5 text-xs border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] appearance-none rounded-sm"
                  >
                    <option value="">All types</option>
                    {["Title Deed", "Sale Deed", "Registration Certificate", "Tax Receipt", "NOC", "Identity Proof", "Other"].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
                </div>
                {(docFilterUnit || docFilterType) && (
                  <button
                    data-testid="doc-filter-clear"
                    onClick={() => { setDocFilterUnit(""); setDocFilterType(""); }}
                    className="text-xs text-[#9C7C54] hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {(() => {
              const filtered = documents.filter(d =>
                (!docFilterUnit || d.unit_id === docFilterUnit) &&
                (!docFilterType || d.document_type === docFilterType)
              );
              if (filtered.length === 0) {
                return (
                  <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-8 text-center text-[#737373] rounded-sm" data-testid="docs-empty-state">
                    <FolderOpen size={32} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">
                      {documents.length === 0 ? "No documents uploaded yet" : "No documents match the current filters"}
                    </p>
                  </div>
                );
              }
              return (
                <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] rounded-sm overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]" data-testid="admin-documents-table">
                    <thead className="bg-[#F5F3F0] dark:bg-[#0F1115]">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373]">Unit</th>
                        <th className="px-4 py-2.5 text-left text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373]">Owner</th>
                        <th className="px-4 py-2.5 text-left text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373]">Type</th>
                        <th className="px-4 py-2.5 text-left text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373]">File</th>
                        <th className="px-4 py-2.5 text-left text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373]">Uploaded</th>
                        <th className="px-4 py-2.5 text-right text-[10px] tracking-[0.15em] uppercase font-semibold text-[#737373]">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5] dark:divide-[#272A30]">
                      {filtered.map((d) => (
                        <tr
                          key={d.id}
                          data-testid={`admin-doc-row-${d.id}`}
                          className="hover:bg-[#F9F8F6] dark:hover:bg-[#0F1115]/60 transition-colors"
                        >
                          <td className="px-4 py-2.5 font-semibold text-[#1A1A1A] dark:text-[#EAEAEA]">{d.unit_id}</td>
                          <td className="px-4 py-2.5 text-[#737373]">{d.owner_name || "—"}</td>
                          <td className="px-4 py-2.5">
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-[#F5EDE0] text-[#7a5f3e] dark:bg-[#2A2318] dark:text-[#C0A062]">
                              {d.document_type}
                            </span>
                            {d.label && <div className="text-[10px] text-[#737373] italic mt-0.5">{d.label}</div>}
                          </td>
                          <td className="px-4 py-2.5 text-[#737373] text-xs">
                            <div className="truncate max-w-[220px]">{d.original_filename}</div>
                            <div className="text-[10px]">{(d.size_bytes / 1024).toFixed(0)} KB</div>
                          </td>
                          <td className="px-4 py-2.5 text-[#737373] text-xs">
                            {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              onClick={() => handleDocDownload(d)}
                              data-testid={`admin-doc-download-${d.id}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-[#9C7C54] hover:bg-[#F5EDE0] dark:hover:bg-[#2A2318] rounded-sm transition-colors"
                              title="Download"
                            >
                              <Download size={12} /> Download
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── IMPORT ───────────────────────────────────────────────────────── */}
        {activeTab === "import" && (
          <div>
            <h2 className="font-heading text-xl font-light text-[#1A1A1A] dark:text-[#EAEAEA] mb-4">Excel Import</h2>
            <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-6 mb-4">
              <label className="block text-xs tracking-[0.15em] uppercase text-[#737373] mb-3">Upload Excel File (.xlsx)</label>
              <div className="flex items-center gap-4">
                <input
                  data-testid="excel-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  ref={fileInputRef}
                  onChange={(e) => e.target.files[0] && handleImport(e.target.files[0])}
                  className="text-sm text-[#737373] file:mr-3 file:py-2 file:px-4 file:border file:border-[#9C7C54] file:text-[#9C7C54] file:text-xs file:bg-transparent file:cursor-pointer hover:file:bg-[#9C7C54] hover:file:text-white file:transition-colors"
                />
              </div>
            </div>

            {importData && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="font-heading text-lg font-light text-[#1A1A1A] dark:text-[#EAEAEA]">
                    Preview — {importData.parsed?.length || 0} rows parsed
                  </h3>
                  {importData.flagged?.length > 0 && (
                    <div className="flex items-center gap-1.5 text-amber-600 text-sm">
                      <AlertTriangle size={14} />
                      {importData.flagged.length} flagged rows
                    </div>
                  )}
                </div>
                <div className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#F5F3F0] dark:bg-[#0F1115]">
                      <tr>
                        {["Unit ID","Owner","Phone","Floor","Flags"].map((h) => (
                          <th key={h} className="px-3 py-2 text-left text-[10px] tracking-[0.15em] uppercase text-[#737373] font-semibold border-b border-[#E5E5E5] dark:border-[#272A30]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importData.parsed?.map((row, i) => (
                        <tr key={i} className={`border-b border-[#E5E5E5] dark:border-[#272A30] ${row.flags?.length > 0 ? "bg-amber-50 dark:bg-amber-950/20" : ""}`}>
                          <td className="px-3 py-2 font-semibold">{row.unit_id}</td>
                          <td className="px-3 py-2 text-[#737373]">{row.owner || "—"}</td>
                          <td className="px-3 py-2 font-mono text-[#737373]">{row.phone || "—"}</td>
                          <td className="px-3 py-2 text-[#737373]">{row.floor || "—"}</td>
                          <td className="px-3 py-2">
                            {row.flags?.map((f) => (
                              <span key={f} className="mr-1 text-amber-600 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded-sm text-[10px]">{f}</span>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Confirm import action */}
                <div className="mt-4 flex items-center justify-between bg-[#F9F8F6] dark:bg-[#0F1115] border border-[#E5E5E5] dark:border-[#272A30] px-5 py-3">
                  <span className="text-xs text-[#737373]">
                    Ready to commit <strong className="text-[#1A1A1A] dark:text-[#EAEAEA]">{importData.parsed?.length || 0}</strong> rows
                    {importData.flagged?.length > 0 && (
                      <span className="ml-1 text-amber-600">({importData.flagged.length} flagged)</span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setImportData(null)}
                      className="px-3 py-1.5 border border-[#E5E5E5] dark:border-[#272A30] text-xs text-[#737373] hover:border-[#9C7C54] transition-colors rounded-sm">
                      Discard
                    </button>
                    <button data-testid="confirm-import-btn" onClick={handleConfirmImport} disabled={confirming}
                      className="flex items-center gap-1.5 px-5 py-1.5 bg-[#9C7C54] text-white text-xs tracking-wide hover:bg-[#7a5f3e] disabled:opacity-50 transition-colors rounded-sm">
                      <Check size={12} /> {confirming ? "Saving…" : "Confirm & Save to Directory"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── FLOOR PLANS ──────────────────────────────────────────────────── */}
        {activeTab === "floorplans" && (
          <div>
            <h2 className="font-heading text-xl font-light text-[#1A1A1A] dark:text-[#EAEAEA] mb-4">Floor Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { label: "Ground Floor", floor: "GF", wing: "" },
                { label: "First Floor · Block A", floor: "FF", wing: "R" },
                { label: "First Floor · Block B", floor: "FF", wing: "L" },
              ].map((fp, i) => {
                const existing = floorPlans.find((p) => p.floor === fp.floor && p.wing === fp.wing);
                const refKey = ["GF","FFR","FFL"][i];
                const fpUrl = existing ? `${API}/files/${existing.storage_path}` : null;
                return (
                  <div key={fp.label} className="bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] p-5">
                    <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#9C7C54] mb-3">{fp.label}</div>
                    {existing ? (
                      <div className="mb-3">
                        <div className="mb-2 overflow-hidden border border-[#E5E5E5] dark:border-[#272A30]" style={{ maxHeight: "100px" }}>
                          <img src={fpUrl} alt={fp.label} style={{ width: "100%", objectFit: "cover", objectPosition: "top" }} />
                        </div>
                        <p className="text-[10px] text-emerald-600 flex items-center gap-1">
                          <Check size={10} /> Uploaded
                        </p>
                      </div>
                    ) : (
                      <div className="mb-3 p-2 bg-[#F9F8F6] dark:bg-[#0F1115] border border-[#E5E5E5] dark:border-[#272A30] rounded-sm">
                        <p className="text-xs text-[#737373]">No image uploaded yet</p>
                      </div>
                    )}
                    <input
                      data-testid={`fp-upload-${fp.floor}-${fp.wing}`}
                      type="file"
                      accept="image/*"
                      ref={fpRefs[refKey]}
                      onChange={(e) => e.target.files[0] && handleFloorPlanUpload(fp.floor, fp.wing, e.target.files[0])}
                      className="w-full text-xs text-[#737373] file:mr-2 file:py-1.5 file:px-3 file:border file:border-[#9C7C54] file:text-[#9C7C54] file:text-xs file:bg-transparent file:cursor-pointer hover:file:bg-[#9C7C54] hover:file:text-white file:transition-colors mb-3"
                    />
                    {existing && (
                      <button
                        data-testid={`edit-hotspots-${fp.floor}-${fp.wing}`}
                        onClick={() => setHotspotEditor({ floor: fp.floor, wing: fp.wing, url: fpUrl })}
                        className="w-full py-2 border border-[#9C7C54] text-[#9C7C54] text-xs tracking-wide hover:bg-[#9C7C54] hover:text-white transition-colors rounded-sm"
                      >
                        Edit Hotspots
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Hotspot editor modal */}
        {hotspotEditor && (
          <HotspotEditor
            floor={hotspotEditor.floor}
            wing={hotspotEditor.wing}
            floorPlanUrl={hotspotEditor.url}
            onClose={() => setHotspotEditor(null)}
          />
        )}
      </div>
    </div>
  );
}

function AddUnitForm({ onAdd, onCancel }) {
  const [form, setForm] = useState({ unit_id: "", floor: "GF", wing: "", size_sqft: 0, occupancy_status: "vacant" });
  return (
    <div className="bg-white dark:bg-[#1A1D24] border border-[#9C7C54]/40 p-5 mb-4 animate-slide-up">
      <h3 className="font-heading text-base font-light text-[#1A1A1A] dark:text-[#EAEAEA] mb-4">Add New Unit</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: "unit_id", label: "Unit ID", type: "text", placeholder: "H-XX" },
          { key: "floor", label: "Floor", type: "select", options: ["GF","FF"] },
          { key: "wing", label: "Wing", type: "select", options: ["","R","L"] },
          { key: "size_sqft", label: "Size (sqft)", type: "number" },
          { key: "occupancy_status", label: "Status", type: "select", options: ["vacant","occupied"] },
        ].map((f) => (
          <div key={f.key}>
            <label className="block text-[10px] uppercase tracking-wide text-[#737373] mb-1">{f.label}</label>
            {f.type === "select" ? (
              <select
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="w-full px-2 py-2 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-xs text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] rounded-sm"
              >
                {f.options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
              </select>
            ) : (
              <input
                type={f.type}
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? +e.target.value : e.target.value })}
                placeholder={f.placeholder}
                className="w-full px-2 py-2 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] text-xs text-[#1A1A1A] dark:text-[#EAEAEA] focus:outline-none focus:ring-1 focus:ring-[#9C7C54] rounded-sm"
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={() => onAdd(form)} data-testid="confirm-add-unit-btn"
          className="px-4 py-2 bg-[#9C7C54] text-white text-xs tracking-wide hover:bg-[#7a5f3e] transition-colors rounded-sm">
          Add Unit
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 border border-[#E5E5E5] dark:border-[#272A30] text-xs text-[#737373] hover:border-[#9C7C54] transition-colors rounded-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}
